import React, { useState } from 'react';
import { Copy, Download, AlertCircle, X, RefreshCw } from 'lucide-react';
import './MFARecoveryCodesModal.css';

/**
 * Recovery Codes Modal - Displays recovery codes with copy/download options.
 */
export const MFARecoveryCodesModal = ({ isOpen, onClose, recoveryCodes, onRegenerate }) => {
  const [copied, setCopied] = useState(false);

  const handleCopyAll = () => {
    if (!recoveryCodes || recoveryCodes.length === 0) return;
    const text = recoveryCodes.join('\n');
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDownload = () => {
    if (!recoveryCodes || recoveryCodes.length === 0) return;
    const content = recoveryCodes.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mfa-recovery-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleRegenerate = () => {
    if (window.confirm('Are you sure you want to generate new recovery codes? Your old codes will no longer work.')) {
      onRegenerate();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="mfa-recovery-codes-modal-overlay" onClick={onClose}>
      <div className="mfa-recovery-codes-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mfa-recovery-codes-header">
          <AlertCircle size={24} />
          <div>
            <h2>Recovery Codes</h2>
            <p className="mfa-recovery-codes-subtitle">Save these codes in a safe place</p>
          </div>
          <button className="mfa-recovery-codes-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="mfa-recovery-codes-warning">
          <AlertCircle size={16} />
          <p>
            <strong>Important:</strong> These codes can be used to access your account if you lose access to your authenticator device. Each code can only be used once. Store them securely.
          </p>
        </div>

        {recoveryCodes && recoveryCodes.length > 0 ? (
          <>
            <div className="mfa-recovery-codes-container">
              {recoveryCodes.map((code, index) => (
                <div key={index} className="mfa-recovery-code-item">
                  <code>{code}</code>
                </div>
              ))}
            </div>

            <div className="mfa-recovery-codes-actions">
              <button
                type="button"
                onClick={handleCopyAll}
                className="btn-secondary"
              >
                <Copy size={16} />
                {copied ? 'Copied!' : 'Copy All'}
              </button>
              <button
                type="button"
                onClick={handleDownload}
                className="btn-secondary"
              >
                <Download size={16} />
                Download
              </button>
            </div>

            {onRegenerate && (
              <div className="mfa-recovery-codes-regenerate">
                <button
                  type="button"
                  onClick={handleRegenerate}
                  className="btn-secondary btn-regenerate"
                >
                  <RefreshCw size={16} />
                  Generate New Codes
                </button>
                <small>This will invalidate your current recovery codes</small>
              </div>
            )}
          </>
        ) : (
          <div className="mfa-recovery-codes-empty">
            <p>No recovery codes available. Generate new codes to continue.</p>
            {onRegenerate && (
              <button
                type="button"
                onClick={handleRegenerate}
                className="btn-primary"
              >
                Generate Recovery Codes
              </button>
            )}
          </div>
        )}

        <div className="mfa-recovery-codes-footer">
          <button type="button" onClick={onClose} className="btn-primary">
            I've Saved My Codes
          </button>
        </div>
      </div>
    </div>
  );
};
